import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from './components/Header';
import URLShortener from './components/URLShortener';
import URLList from './components/URLList';
import RedirectHandler from './components/RedirectHandler';
import Footer from './components/Footer';
import LoginForm from './components/LoginForm';
import { isAuthenticated } from './utils/auth';
import './App.css';

function App() {
  const [urls, setUrls] = useState([]);
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check authentication status on app load
    const checkAuth = () => {
      const authStatus = isAuthenticated();
      setAuthenticated(authStatus);
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    // Load URLs only if authenticated
    if (authenticated) {
      const savedUrls = localStorage.getItem('shortenedUrls');
      if (savedUrls) {
        setUrls(JSON.parse(savedUrls));
      }
    }
  }, [authenticated]);

  const handleLogin = () => {
    setAuthenticated(true);
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setUrls([]); // Clear URLs from state on logout
  };

  const addUrl = (urlData) => {
    const newUrls = [urlData, ...urls];
    setUrls(newUrls);
    localStorage.setItem('shortenedUrls', JSON.stringify(newUrls));
  };

  const deleteUrl = (id) => {
    const newUrls = urls.filter(url => url.id !== id);
    setUrls(newUrls);
    localStorage.setItem('shortenedUrls', JSON.stringify(newUrls));
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!authenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <Routes>
          <Route path="/:shortCode" element={<RedirectHandler urls={urls} />} />
          <Route path="/" element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col min-h-screen"
            >
              <Header onLogout={handleLogout} />
              <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
                <URLShortener onAddUrl={addUrl} existingUrls={urls} />
                <URLList urls={urls} onDeleteUrl={deleteUrl} />
              </main>
              <Footer />
            </motion.div>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;